import type { SceneObject } from '../types/scene'
import { registry } from '../components-lib/registry'
import { resolveUniformScaledParams } from '../utils/uniformScaleParams'

interface Bounds {
  id: string
  x: number
  z: number
  w: number
  d: number
  minX: number
  maxX: number
  minZ: number
  maxZ: number
}

const EDGE_SNAP_THRESHOLD = 1    // 边对齐的最大吸附距离（米）
const CENTER_SNAP_THRESHOLD = 1.5 // 中心对齐的最大吸附距离（米）

function getBoundsFor(obj: SceneObject, overrideX?: number, overrideZ?: number): Bounds | null {
  const def = registry.get(obj.type)
  if (!def || !def.getBounds) return null

  const b = def.getBounds(resolveUniformScaledParams(def, obj.params ?? {}))
  const cx = overrideX !== undefined ? overrideX : obj.position[0]
  const cz = overrideZ !== undefined ? overrideZ : obj.position[2]
  const hw = b.w / 2
  const hd = b.d / 2

  return {
    id: obj.id,
    x: cx,
    z: cz,
    w: b.w,
    d: b.d,
    minX: cx - hw,
    maxX: cx + hw,
    minZ: cz - hd,
    maxZ: cz + hd
  }
}

export class SnapHelper {
  /**
   * 计算靠近其它对象边界时的吸附位置（建筑/设备等通用）
   * @param x 当前拖拽落点 X（已做网格吸附）
   * @param z 当前拖拽落点 Z（已做网格吸附）
   * @param objects 当前场景对象列表
   * @param movingId 正在拖拽的对象 id
   */
  snapPosition(
    x: number,
    z: number,
    objects: SceneObject[],
    movingId?: string
  ): { x: number; z: number } | null {
    if (!movingId) return null

    const moving = objects.find(o => o.id === movingId)
    if (!moving) return null

    const movingBounds = getBoundsFor(moving, x, z)
    if (!movingBounds) return null

    let bestX: { value: number; dist: number } | null = null
    let bestZ: { value: number; dist: number } | null = null

    for (const obj of objects) {
      if (obj.id === movingId) continue

      const b = getBoundsFor(obj)
      if (!b) continue

      // ── X 方向边对齐 ────────────────────────────────
      const leftToRight = Math.abs(movingBounds.minX - b.maxX)
      if (leftToRight <= EDGE_SNAP_THRESHOLD) {
        const newCx = b.maxX + movingBounds.w / 2
        if (!bestX || leftToRight < bestX.dist) {
          bestX = { value: newCx, dist: leftToRight }
        }
      }

      const rightToLeft = Math.abs(movingBounds.maxX - b.minX)
      if (rightToLeft <= EDGE_SNAP_THRESHOLD) {
        const newCx = b.minX - movingBounds.w / 2
        if (!bestX || rightToLeft < bestX.dist) {
          bestX = { value: newCx, dist: rightToLeft }
        }
      }

      // X 中心对齐
      const centerX = Math.abs(movingBounds.x - b.x)
      if (centerX <= CENTER_SNAP_THRESHOLD) {
        if (!bestX || centerX < bestX.dist) {
          bestX = { value: b.x, dist: centerX }
        }
      }

      // ── Z 方向边对齐 ────────────────────────────────
      const frontToBack = Math.abs(movingBounds.minZ - b.maxZ)
      if (frontToBack <= EDGE_SNAP_THRESHOLD) {
        const newCz = b.maxZ + movingBounds.d / 2
        if (!bestZ || frontToBack < bestZ.dist) {
          bestZ = { value: newCz, dist: frontToBack }
        }
      }

      const backToFront = Math.abs(movingBounds.maxZ - b.minZ)
      if (backToFront <= EDGE_SNAP_THRESHOLD) {
        const newCz = b.minZ - movingBounds.d / 2
        if (!bestZ || backToFront < bestZ.dist) {
          bestZ = { value: newCz, dist: backToFront }
        }
      }

      // Z 中心对齐
      const centerZ = Math.abs(movingBounds.z - b.z)
      if (centerZ <= CENTER_SNAP_THRESHOLD) {
        if (!bestZ || centerZ < bestZ.dist) {
          bestZ = { value: b.z, dist: centerZ }
        }
      }
    }

    if (!bestX && !bestZ) return null

    return {
      x: bestX ? bestX.value : x,
      z: bestZ ? bestZ.value : z
    }
  }
}

